import { useState, useRef, useEffect, useCallback } from "react";
import { trackingCards } from "@/mocks/homeData";

export default function TrackingSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, [checkScroll]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -340 : 340, behavior: "smooth" });
  };

  return (
    <section className="w-full bg-foreground-950 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-0 mb-12 md:mb-16">
          <div className="flex-1">
            <span className="inline-block px-4 py-1.5 rounded-full border border-background-300/30 text-background-300 text-xs md:text-sm font-medium whitespace-nowrap">
              24H 实时追踪
            </span>
          </div>
          <div className="lg:w-2/3">
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
              吃瓜群众深夜基地
            </h2>
          </div>
        </div>

        {/* Scrollable cards */}
        <div className="relative">
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-900/80 text-background-200 hover:bg-background-800 transition-colors cursor-pointer"
              aria-label="向左滚动"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 md:px-6"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {trackingCards.map((card) => (
              <div
                key={card.id}
                className="flex-shrink-0 w-[280px] md:w-[320px] bg-background-100 rounded-2xl overflow-hidden group cursor-pointer transition-transform hover:-translate-y-1"
              >
                <div className="relative w-full h-[200px] md:h-[240px] overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    title={card.title}
                  />
                </div>
                <div className="p-4 md:p-5 bg-background-100">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-accent-500 font-medium">{card.category}</span>
                    <span className="text-xs text-foreground-500">{card.time}</span>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2">
                    {card.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-900/80 text-background-200 hover:bg-background-800 transition-colors cursor-pointer"
              aria-label="向右滚动"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}